import {
  MARKET_ACCOUNT_PERMISSIONS_FILTER,
  UpdateActionEnum,
  useEndpointsContext,
  useGlobalToasts,
  useMarketAccountsContext,
  useObservable,
  useObservableValue,
  useSubscription,
  type IMarketAccountApiPermissionFilter,
  type MarketAccount,
  type MarketAccountApiPermissionFilter,
  type MarketTypeEnum,
  type SubscriptionResponse,
} from '@talos/kyoko';
import { createContext, useCallback, useContext, useMemo, useRef, type PropsWithChildren } from 'react';
import { Subject, merge, scan } from 'rxjs';
import { scanToMap } from '../components/OMS/testutils/testutilfns';
import { saveMarketAccountPermissionFilters } from './saveMarketAccountPermissionFilters';

export const MarketAccountPermissionFiltersContext = createContext<
  MarketAccountPermissionFiltersContextProps | undefined
>(undefined);
MarketAccountPermissionFiltersContext.displayName = 'MarketAccountPermissionFiltersContext';

export type MarketAccountPermissionFiltersContextProps = {
  marketAccountFilterIDsBySubAccountName: Map<string, Set<string>> | undefined;
  marketAccountPermissionFiltersByFilterID: Map<string, MarketAccountApiPermissionFilter> | undefined;
  updateMarketAccountPermissionFilters: (updatedPermissions: MarketAccountApiPermissionFilter[]) => Promise<string[]>;
};

export type MarketAccountSubAccountPermissionRow = {
  MarketAccount: Pick<MarketAccount, 'Name'> & { MarketType: MarketTypeEnum | '' };
  FilterPermission: MarketAccountApiPermissionFilter;
};

export function useMarketAccountPermissionFilters() {
  const context = useContext(MarketAccountPermissionFiltersContext);
  if (context === undefined) {
    throw new Error(
      'Missing MarketAccountPermissionFiltersContext.Provider further up in the tree. Did you forget to add it?'
    );
  }
  return context;
}

const MARKET_ACCOUNT_PERMISSION_REQUEST = {
  name: MARKET_ACCOUNT_PERMISSIONS_FILTER,
  tag: 'MarketAccountPermissionFiltersProvider',
  // These records are small so we can get more of them at once. Default on backend is 500.
  limit: 5000,
};

export const MarketAccountPermissionFiltersProvider = function MarketAccountPermissionFiltersProvider({
  children,
}: PropsWithChildren) {
  const { data: marketAccountPermissionsFilterRawObs } = useSubscription<IMarketAccountApiPermissionFilter>(
    MARKET_ACCOUNT_PERMISSION_REQUEST
  );

  const marketAccountPermissionsFilterUpdatesSubject = useRef(
    new Subject<SubscriptionResponse<MarketAccountApiPermissionFilter, string>>()
  );
  const marketAccountPermissionsFilterUpdatesObs = useObservable<
    SubscriptionResponse<MarketAccountApiPermissionFilter, string>
  >(() => marketAccountPermissionsFilterUpdatesSubject.current.asObservable(), []);

  const { orgApiEndpoint } = useEndpointsContext();
  const { marketAccountDisplayNameByName } = useMarketAccountsContext();
  const { add: addToast } = useGlobalToasts();

  const updateMarketAccountPermissionFilters = useCallback(
    async (updatedPermissions: MarketAccountApiPermissionFilter[]): Promise<string[]> => {
      if (!orgApiEndpoint) {
        return [];
      }

      const { applied, failedFilterIDs } = await saveMarketAccountPermissionFilters({
        permissions: updatedPermissions,
        deleteUrl: permission => `${orgApiEndpoint}/permission-filters/marketaccounts/${permission.FilterID}`,
        createUrl: `${orgApiEndpoint}/permission-filters/marketaccounts`,
        createBody: permission => ({
          Action: permission.Action,
          Filter: permission.Filter,
          Subject: permission.Subject,
        }),
        marketAccountName: permission =>
          marketAccountDisplayNameByName.get(permission.Filter.MarketAccount) || permission.Filter.MarketAccount,
        addToast,
      });

      marketAccountPermissionsFilterUpdatesSubject.current.next({
        type: '',
        tag: 'REST_RESPONSE',
        ts: '',
        data: applied,
      });

      return failedFilterIDs;
    },
    [orgApiEndpoint, marketAccountDisplayNameByName, addToast]
  );

  const marketAccountPermissionFiltersObs = useObservableValue(
    () =>
      merge(marketAccountPermissionsFilterRawObs, marketAccountPermissionsFilterUpdatesObs).pipe(
        scan(
          ({ marketAccountPermissionFiltersByFilterID, marketAccountFilterIDsBySubAccountName }, json) => {
            const nextMarketAccountPermissionFiltersByFilterID = json.initial
              ? new Map<string, MarketAccountApiPermissionFilter>()
              : new Map(marketAccountPermissionFiltersByFilterID);
            const nextMarketAccountFilterIDsBySubAccountName = new Map(marketAccountFilterIDsBySubAccountName);

            for (const data of json.data) {
              // per filterID
              const key = data.FilterID;
              if (key) {
                if (data.UpdateAction === UpdateActionEnum.Remove) {
                  nextMarketAccountPermissionFiltersByFilterID.delete(key);
                } else {
                  nextMarketAccountPermissionFiltersByFilterID.set(key, data);
                }
              }

              const subAccount = data.Subject.SubAccount;
              const marketAccount = data.Filter.MarketAccount;
              if (subAccount && marketAccount) {
                const filterIDs = new Set(nextMarketAccountFilterIDsBySubAccountName.get(subAccount) ?? []);
                data.UpdateAction === UpdateActionEnum.Remove
                  ? filterIDs.delete(data.FilterID)
                  : filterIDs.add(data.FilterID);
                nextMarketAccountFilterIDsBySubAccountName.set(subAccount, filterIDs);
              }
            }

            return {
              marketAccountPermissionFiltersByFilterID: nextMarketAccountPermissionFiltersByFilterID,
              marketAccountFilterIDsBySubAccountName: nextMarketAccountFilterIDsBySubAccountName,
            };
          },
          {
            marketAccountPermissionFiltersByFilterID: new Map<string, MarketAccountApiPermissionFilter>(),
            marketAccountFilterIDsBySubAccountName: new Map<string, Set<string>>(),
          }
        )
      ),
    [marketAccountPermissionsFilterRawObs, marketAccountPermissionsFilterUpdatesObs],
    {
      marketAccountPermissionFiltersByFilterID: new Map<string, MarketAccountApiPermissionFilter>(),
      marketAccountFilterIDsBySubAccountName: new Map<string, Set<string>>(),
    }
  );

  const { marketAccountFilterIDsBySubAccountName, marketAccountPermissionFiltersByFilterID } =
    marketAccountPermissionFiltersObs;

  const value = useMemo(
    () => ({
      marketAccountPermissionFiltersByFilterID,
      marketAccountFilterIDsBySubAccountName,
      updateMarketAccountPermissionFilters,
    }),
    [
      marketAccountPermissionFiltersByFilterID,
      marketAccountFilterIDsBySubAccountName,
      updateMarketAccountPermissionFilters,
    ]
  );

  return (
    <MarketAccountPermissionFiltersContext.Provider value={value}>
      {children}
    </MarketAccountPermissionFiltersContext.Provider>
  );
};

/** Test utility function to help build one of the data sets exposed by this provider */
export function testBuildMarketAccountPermissionFiltersByFilterID(
  permissions: MarketAccountApiPermissionFilter[]
): Map<string, MarketAccountApiPermissionFilter> {
  return scanToMap(permissions, p => p.FilterID);
}

/** Test utility function to help build one of the data sets exposed by this provider */
export function testBuildMarketAccountFilterIDsBySubAccountName(
  permissions: MarketAccountApiPermissionFilter[]
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  permissions.forEach(p => {
    const set = map.get(p.Subject.SubAccount) ?? new Set();
    set.add(p.FilterID);
    map.set(p.Subject.SubAccount, set);
  });

  return map;
}

/** Test utility function to help build all the data sets and other functions exposed by this provider */
export function testBuildMarketAccountPermissionFiltersContextProps(
  permissions: MarketAccountApiPermissionFilter[]
): MarketAccountPermissionFiltersContextProps {
  return {
    marketAccountFilterIDsBySubAccountName: testBuildMarketAccountFilterIDsBySubAccountName(permissions),
    marketAccountPermissionFiltersByFilterID: testBuildMarketAccountPermissionFiltersByFilterID(permissions),
    updateMarketAccountPermissionFilters: () => Promise.resolve([]),
  };
}
